import { NgClass } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { Context } from 'src/app/controls/advanced-gridview/models/context';
import { Box } from 'src/app/models/box';
import { BoxGroup } from 'src/app/models/box-group';
import { BoxesService } from 'src/app/services/boxes.service';
import { MemoryService } from 'src/app/services/memory.service';
import { BoxesTreeItemComponent } from '../box-tree-item/box-tree-item.component';

@Component({
    selector: 'app-box-group-tree-item',
    templateUrl: './box-group-tree-item.component.html',
    styleUrls: ['./box-group-tree-item.component.scss'],
    imports: [MatIconModule, NgClass, BoxesTreeItemComponent]
})
export class BoxGroupTreeComponent implements OnInit {
    /**
     * Instance du groupe affiché par le noeud courant.
     */
    @Input() boxGroup: BoxGroup;

    /**
     * Indique si le noeud est affiché déplié (true) ou plié (false).
     */
    @Input() expanded: boolean;

    /**
     * Emis lorsque l'utilisateur clique sur le menu contextuel 'Editer' d'une bannette.
     */
    @Output()
    itemEdited: EventEmitter<Box> = new EventEmitter();

    /**
     * Emis lorsque l'utilisateur clique sur le menu contextuel 'Consulter' d'une bannette.
     */
    @Output()
    itemDisplayed: EventEmitter<Box> = new EventEmitter();

    /**
     * Emis lorsque l'utilisateur clique sur le menu contextuel 'Supprimer' d'une bannette.
     */
    @Output()
    itemDeleted: EventEmitter<Box> = new EventEmitter();

    /**
     * Emis lorsque l'utilisateur clique sur le menu contextuel 'dupliquer' d'une bannette.
     */
    @Output()
    itemDuplicated: EventEmitter<Box> = new EventEmitter();

    /**
     * Liste des noeuds enfants de type bannettes.
     */
    public childrenBoxes: Box[] = [];

    /**
     * Valeur indiquant si l'id est affiché.
     */
    public isIdVisible: boolean;

    /**
     * Constructeur.
     * @param boxesService Service pour gérer les bannettes.
     */
    constructor(
        public boxesService: BoxesService,
        protected memoryService: MemoryService
    ) {
        this.isIdVisible = this.memoryService.getConnectedUser().params.idVisible === true;
    }

    /**
     * Initialise le composant.
     */
    ngOnInit(): void {
        // Chargement des noeuds enfants.
        this.loadChildren();
    }

    /**
     * Charge les noeuds enfants.
     */
    private loadChildren() {
        let context: Context = { property: 'boxGroupId', value: this.boxGroup.id };

        this.boxesService.List(context, null, null, null).subscribe((x) => {
            this.childrenBoxes = x.value;
        });
    }

    /**
     * Appellé lorsque l'utilisateur clique sur l'icône pour plier / déplier le groupe.
     */
    public onExpand(): void {
        this.expanded = !this.expanded;
    }

    /**
     * Appellé lorsque l'utilisateur sélectionne 'Consulter' dans le menu contextuel.
     * @param box Bannette à afficher.
     */
    public onBoxDisplayed(box: Box) {
        this.itemDisplayed.emit(box);
    }

    /**
     * Appellé lorsque l'utilisateur sélectionne 'Editer' dans le menu contextuel.
     * @param user Bannette à editer.
     */
    public onBoxEdited(box: Box) {
        this.itemEdited.emit(box);
    }

    /**
     * Appellé lorsque l'utilisateur sélectionne 'Dupliquer' dans le menu contextuel d'une bannette.
     * @param user Bannette sélectionnée.
     */
    public onBoxDuplicated(box: Box) {
        this.itemDuplicated.emit(box);
    }

    /**
     * Appellé lorsque l'utilisateur sélectionne 'Supprimer' dans le menu contextuel.
     * @param user Bannette à supprimer.
     */
    public onBoxDeleted(box: Box) {
        this.itemDeleted.emit(box);
    }

    /**
     * Raffraichit l'affichage des noeuds enfants de ce groupe de bannettes s'il correspond à l'id spécifié.
     * Fait suivre la demande de raffraichissement aux noeuds enfants.
     * @param boxGroupId : Id du groupe de bannettes à raffraichir.
     */
    public refresh(boxGroupId: string) {
        if (this.boxGroup.id === boxGroupId) {
            this.loadChildren();
        }
    }
}
